
import React, { useState } from 'react';
import { ArrowLeft, Sparkles, User, Globe } from 'lucide-react';
import { Language } from '../types';

interface Props {
  onNext: (name: string, lang: Language) => void;
  t: any;
  currentLang: Language;
  onLangChange: (lang: Language) => void;
}

const NameInput: React.FC<Props> = ({ onNext, t, currentLang, onLangChange }) => {
  const [name, setName] = useState('');

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 p-6 animate-fade-in relative overflow-hidden">
      {/* Language Switcher on Landing */}
      <div className="absolute top-6 left-6 flex gap-2 z-20">
        <button 
          onClick={() => onLangChange(Language.KURDISH)}
          className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${currentLang === Language.KURDISH ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white text-slate-400 border border-slate-200'}`}
        >
          کوردی
        </button>
        <button 
          onClick={() => onLangChange(Language.PERSIAN)}
          className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${currentLang === Language.PERSIAN ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white text-slate-400 border border-slate-200'}`}
        >
          فارسی
        </button>
      </div>

      {/* Decorative Background Elements */}
      <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-indigo-100 rounded-full blur-3xl opacity-40 animate-pulse"></div>
      <div className="absolute bottom-[-10%] left-[-10%] w-96 h-96 bg-pink-100 rounded-full blur-3xl opacity-40 animate-pulse" style={{animationDelay: '1s'}}></div>

      <div className="relative z-10 w-full max-w-md flex flex-col items-center">
        <div className="mb-10 relative">
            <div className="w-24 h-24 bg-white rounded-[2rem] shadow-xl flex items-center justify-center rotate-6 transition-transform hover:rotate-0 duration-500">
                <Sparkles className="w-12 h-12 text-indigo-500" />
            </div>
            <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center border-4 border-slate-50">
                <span className="text-xl">👋</span>
            </div>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-slate-800 mb-6 tracking-tight text-center leading-tight">
          {t.onboarding_title}
        </h1>
        
        <p className="text-slate-500 mb-10 text-lg text-center leading-relaxed max-w-xs mx-auto">
          {t.onboarding_subtitle}
        </p>

        <div className="w-full bg-white p-2 rounded-2xl shadow-lg border border-slate-100 flex items-center gap-3 mb-8 transition-all focus-within:ring-4 focus-within:ring-indigo-50 focus-within:border-indigo-300 transform focus-within:-translate-y-1 duration-300">
            <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400">
                <User className="w-6 h-6" />
            </div>
            <input 
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && name.trim() && onNext(name.trim(), currentLang)}
                placeholder={t.name_placeholder}
                className="flex-1 bg-transparent border-none outline-none py-3 text-lg text-slate-800 placeholder:text-slate-300 font-medium"
                autoFocus
            />
        </div>

        <button
            onClick={() => name.trim() && onNext(name.trim(), currentLang)}
            disabled={!name.trim()}
            className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold text-lg hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-200 disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed flex items-center justify-center gap-3 group relative overflow-hidden"
        >
            <span className="relative z-10">{t.continue}</span>
            <ArrowLeft className="w-5 h-5 relative z-10 group-hover:-translate-x-1 transition-transform rtl:rotate-180" />
            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
        </button>
        
        <p className="mt-6 text-xs text-slate-400">
            {t.onboarding_privacy}
        </p>
      </div>
    </div>
  );
};

export default NameInput;
